// Linkify emails, phone numbers and street addresses inside sanitized email HTML.
//
// Runs on a DOM (not a regex on the HTML string) so it can't be tricked into
// linking text inside href attributes, <script> blocks, or existing <a> tags.
// DOMPurify has already stripped scripts/event handlers before we get here.
//
// - Emails   → <a href="mailto:…">
// - Phones   → <a href="sms:…">  (opens iMessage on iOS, Messages on Android)
// - Addresses→ <a href="bmail-map:…">  (opens the maps/copy menu, 2026-10-30)
//
// Existing anchors are left alone so we don't double-wrap or clobber real links.

const EMAIL_RE = /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g;
// US-style phone numbers: +1 (415) 243-0451, 415-243-0451, 415.243.0451, (415) 243 0451
// Require word boundaries on both sides to avoid matching inside longer digit strings
// (dates, invoice IDs, serial numbers). The leading \b prevents matching a phone
// that's actually part of a longer token like "2024-08-12" or "order#123-456-7890".
// We also require the match to be preceded by whitespace, start-of-string, or a
// non-digit character to further reduce false positives.
const PHONE_RE = /(?:^|(?<=\s|[(:;]|))(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/g;

// US street addresses (2026-10-30, Brian: tapping an address should offer maps or copy).
// The shape: a leading house number (with optional 5th/2nd-style ordinal), 1–5 street-name
// words, a road type — then OPTIONALLY a ", Oakland, CA 94607"-style city + state-zip tail.
// 🚩 The city tail is lookahead-guarded (may only be followed by the state-zip, sentence
// punctuation, or the end) so "2 Main St, John went home" keeps "John" out of the address,
// and "…, Oakland or call 415-243-0451" stops at "2 Main St" — a greedy tail would swallow
// the phone into one giant "address", and the overlap filter would then kill the phone link.
// Road types cover what charter guests actually receive (streets, avenues, marina-ish names,
// and Broadway — the Charlotte pickup at 2 Broadway). "Jack London Square" has no house
// number and stays plain text, deliberately. Single-letter compass roads (W/E/N/S —
// "548 Embarcadero W") are included for waterfront/marina addresses.
const ADDRESS_RE = /\b\d{1,5}(?:st|nd|rd|th)?\s+(?:[A-Za-z0-9][A-Za-z.'-]*\s+){0,5}(?:St|Street|Ave|Avenue|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Way|Pl|Place|Ct|Court|Ter|Terrace|Cir|Circle|Pkwy|Parkway|Hwy|Highway|Sq|Square|Plaza|Wharf|Marina|Broadway|W|E|N|S)\b\.?(?:\s*,\s*(?:[A-Z][A-Za-z.'-]*\s+){0,2}[A-Z][A-Za-z.'-]*(?=\s*,?\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?|[.;)]|\s*$))?(?:\s*,?\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)?/g;

// Tags whose text content should never be linkified.
const SKIP_TAGS = new Set(['A', 'SCRIPT', 'STYLE', 'TITLE', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON']);

function makeLink(href: string, text: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  a.textContent = text;
  a.className = 'bmail-linkified';
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  return a;
}

export type ContactMatch = { start: number; end: number; href: string };

/**
 * Where the emails and phone numbers are in a run of text, left to right, without overlaps.
 * Shared by the HTML walker below and the plain-text body, which renders the links in React.
 */
export function findContacts(text: string): ContactMatch[] {
  if (!text || text.trim().length < 4) return [];
  const matches: ContactMatch[] = [];

  EMAIL_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = EMAIL_RE.exec(text)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, href: `mailto:${m[0]}` });
  }
  PHONE_RE.lastIndex = 0;
  while ((m = PHONE_RE.exec(text)) !== null) {
    // Strip non-digits for the sms: href (carriers/Messages app want digits only).
    // Keep only the first leading + and digits; reject if digit count is outside 7–15
    // (international phone numbers range from 7 to 15 digits per ITU-T E.164).
    const rawDigits = m[0].replace(/[^\d+]/g, '');
    const hasPlus = rawDigits.startsWith('+');
    const digitsOnly = rawDigits.replace(/\+/g, '');
    if (digitsOnly.length < 7 || digitsOnly.length > 15) continue;
    const digits = (hasPlus ? '+' : '') + digitsOnly;
    matches.push({ start: m.index, end: m.index + m[0].length, href: `sms:${digits}` });
  }
  ADDRESS_RE.lastIndex = 0;
  while ((m = ADDRESS_RE.exec(text)) !== null) {
    // One-line, single-spaced address text for the href and the menu's label.
    const addr = m[0].replace(/\s+/g, ' ').trim();
    if (addr.length >= 8) matches.push({ start: m.index, end: m.index + m[0].length, href: `bmail-map:${addr}` });
  }
  // Sort by position so callers can split left-to-right; drop overlaps (an email match and a
  // phone match shouldn't overlap, but guard anyway).
  matches.sort((a, b) => a.start - b.start);
  let cursor = 0;
  return matches.filter((x) => (x.start >= cursor ? ((cursor = x.end), true) : false));
}

function linkifyTextNode(node: Text): void {
  const text = node.nodeValue || '';
  const matches = findContacts(text);
  if (matches.length === 0) return;

  const parent = node.parentNode;
  if (!parent) return;

  // Replace the text node with a mix of text + <a> nodes.
  const frag = document.createDocumentFragment();
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) {
      frag.appendChild(document.createTextNode(text.slice(cursor, match.start)));
    }
    const linkText = text.slice(match.start, match.end);
    frag.appendChild(makeLink(match.href, linkText));
    cursor = match.end;
  }
  if (cursor < text.length) {
    frag.appendChild(document.createTextNode(text.slice(cursor)));
  }

  parent.replaceChild(frag, node);
}

/**
 * Walk the given root element's text nodes and wrap emails/phone numbers in
 * clickable links. Mutates the DOM in place.
 */
export function linkifyEmailsAndPhones(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Text): number {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      // Skip already-linked text (inside an <a>)
      if (parent.closest('a')) return NodeFilter.FILTER_REJECT;
      const text = node.nodeValue || '';
      if (text.trim().length < 4) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });

  // Collect first, then mutate — mutating during walking breaks TreeWalker.
  const targets: Text[] = [];
  let current: Node | null;
  while ((current = walker.nextNode()) !== null) {
    targets.push(current as Text);
  }
  for (const t of targets) linkifyTextNode(t);
}